import { useEffect, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import Image from "next/image";
import useUser from "@/api/apis";
import { useTrans } from "@/utils/useTrans";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { cn } from "@/libs/utils";
import { Button } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import { Input } from "@/v2-components/ui/input";
import { TextArea } from "@/v2-components/ui/textarea";
import { Label } from "@/v2-components/ui/label";
import { SwitchCase } from "@/v2-components/switch-case";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import {
  buildBulkOrderBody,
  BulkOrderInquiryInput,
  submitBulkOrderInquiry,
} from "@/libs/bulkOrderMailto";
import { isEmail } from "@/utils/CommonUtils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  desiredProduct?: string;
};

type Phase = "write" | "sent";

const BODY_MAX = 4000;

export default function BulkOrderInquiryDialog({
  open,
  onOpenChange,
  desiredProduct,
}: Props) {
  const { t } = useTrans(["gnb"]);
  const { user } = useUser();
  const [phase, setPhase] = useState<Phase>("write");
  const isMobileView = useIsMobileView();

  const form = useForm<BulkOrderInquiryInput>({
    defaultValues: {
      replyTo: "",
      body: buildBulkOrderBody({ desiredProduct }),
      website: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (user?.email)
      form.setValue("replyTo", user.email, { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("overflow-y-auto laptop:max-w-[560px]")}
        fullScreen={isMobileView}
      >
        <DialogHeader className="flex min-h-16 items-center border-b border-coolgray-20 laptop:min-h-fit laptop:border-none">
          <DialogTitle className="sr-only invisible laptop:not-sr-only">
            {t("Bulk Order Inquiry")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("Bulk Order Inquiry")}
          </DialogDescription>
          <DialogClose className="hidden laptop:block" />
          <div
            className={cn(
              "flex flex-1 laptop:hidden",
              phase !== "write" && "justify-end",
            )}
          >
            <DialogClose asChild>
              <button aria-label="close">
                <CrossIcon className="mx-4 h-6 w-6" />
              </button>
            </DialogClose>
          </div>
        </DialogHeader>

        <SwitchCase
          value={phase}
          caseBy={{
            write: <WritePhase form={form} onSent={() => setPhase("sent")} />,
            sent: <SentPhase email={form.getValues("replyTo")} />,
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function WritePhase({
  form,
  onSent,
}: {
  form: UseFormReturn<BulkOrderInquiryInput>;
  onSent: () => void;
}) {
  const { t } = useTrans(["gnb"]);
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (data: BulkOrderInquiryInput) => {
    setErrorMsg("");
    try {
      await submitBulkOrderInquiry(data);
      onSent();
    } catch (e) {
      setErrorMsg(
        e instanceof Error ? e.message : t("Failed to send. Please try again."),
      );
    }
  };

  const bodyLen = form.watch("body").length;

  return (
    <form onSubmit={form.handleSubmit(submit)}>
      <div className="flex flex-col gap-5 px-4 laptop:px-0">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl">{t("Bulk Order Inquiry")}</h2>
          <p className="text-base">
            {t("Fill in the details below. We will reply by email.")}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="bulk-replyto">{t("Your email")}</Label>
          <Input
            id="bulk-replyto"
            type="email"
            required
            placeholder="you@company.com"
            {...form.register("replyTo", {
              required: true,
              validate: (v) => Boolean(isEmail(v)),
            })}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="bulk-body">{t("Inquiry details")}</Label>
          <TextArea
            id="bulk-body"
            className="min-h-[280px] font-mono text-xs"
            maxLength={BODY_MAX}
            {...form.register("body", { required: true, minLength: 20 })}
          />
          <p className="text-end text-xs text-coolgray-40">
            {bodyLen}/{BODY_MAX}
          </p>
        </div>

        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          {...form.register("website")}
          style={{
            position: "absolute",
            left: "-10000px",
            width: "1px",
            height: "1px",
            opacity: 0,
          }}
        />

        {errorMsg && <p className="text-sm text-red-500">{errorMsg}</p>}
      </div>

      <DialogFooter className="mb-16 flex-col justify-end gap-2 px-4 laptop:mb-0 laptop:flex-row laptop:items-center laptop:px-0">
        <DialogClose asChild>
          <Button
            type="button"
            size="sm"
            variant="darkgray-outline"
            shape="pill"
            className="w-full px-6 laptop:w-auto"
            disabled={form.formState.isSubmitting}
          >
            {t("Cancel")}
          </Button>
        </DialogClose>
        <Button
          type="submit"
          size="sm"
          variant="blue-fill"
          shape="pill"
          className="w-full px-6 laptop:w-auto"
          disabled={!form.formState.isValid || form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? t("Sending...") : t("Send inquiry")}
        </Button>
      </DialogFooter>
    </form>
  );
}

function SentPhase({ email }: { email: string }) {
  const { t } = useTrans(["gnb"]);
  return (
    <div className="px-4 laptop:px-0">
      <div className="flex flex-col items-center text-center">
        <div className="relative aspect-square w-[230px]">
          <Image
            src="/images/buzz.png"
            alt="thank you"
            fill
            className="absolute object-cover"
          />
        </div>
        <h2 className="text-4xl">{t("Thanks — we received your inquiry.")}</h2>
        <p className="mt-4 text-lg">
          {t("We will reply to {{email}} shortly.", { email })}
        </p>
      </div>

      <DialogFooter className="mb-16 justify-end gap-2 laptop:mb-0 laptop:px-0">
        <DialogClose asChild>
          <Button
            size="sm"
            variant="blue-fill"
            shape="pill"
            className="w-full px-7 laptop:w-auto"
          >
            {t("Close")}
          </Button>
        </DialogClose>
      </DialogFooter>
    </div>
  );
}
